import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPopper } from "@popperjs/core";

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select items...",
  label,
  className = "",
  maxHeight = "200px",
  showTags = false,
  disabled = false,
  searchable = true,
  clearable = true,
  selectAllOption = true,
  placement = "bottom-start",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const popperInstanceRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Initialize and manage Popper.js instance
  useEffect(() => {
    if (isOpen && triggerRef.current && dropdownRef.current) {
      // Destroy existing instance
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy();
      }

      // Create new Popper instance with enhanced modifiers
      popperInstanceRef.current = createPopper(
        triggerRef.current,
        dropdownRef.current,
        {
          placement: placement,
          strategy: "absolute",
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
                padding: 8,
                altAxis: true,
                altBoundary: true,
              },
            },
            {
              name: "flip",
              options: {
                fallbackPlacements: [
                  "top-start",
                  "top-end",
                  "bottom-end",
                  "right-start",
                  "left-start",
                  "auto",
                ],
                padding: 8,
              },
            },
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
            {
              name: "computeStyles",
              options: {
                adaptive: true,
                gpuAcceleration: true,
              },
            },
            {
              name: "eventListeners",
              options: {
                scroll: true,
                resize: true,
              },
            },
          ],
        }
      );

      // Show dropdown with animation
      if (dropdownRef.current) {
        dropdownRef.current.style.opacity = "1";
        dropdownRef.current.style.visibility = "visible";
        dropdownRef.current.style.transform = "scale(1)";
      }
    }

    return () => {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy();
        popperInstanceRef.current = null;
      }
    };
  }, [isOpen, placement]);

  // Update Popper position when content changes
  useEffect(() => {
    if (popperInstanceRef.current && isOpen) {
      popperInstanceRef.current.update();
    }
  }, [searchTerm, filteredOptions?.length, value.length, isOpen]);

  // Close dropdown when clicking outside or on scroll/resize
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchable]);

  // Handle item selection toggle
  const handleItemToggle = useCallback(
    (optionValue) => {
      if (disabled) return;

      const newValue = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];
      onChange(newValue);
    },
    [value, onChange, disabled]
  );

  // Handle select all filtered items
  const handleSelectAll = useCallback(() => {
    if (disabled) return;

    const allFilteredValues = filteredOptions.map((option) => option.value);
    const newValue = [...new Set([...value, ...allFilteredValues])];
    onChange(newValue);
  }, [filteredOptions, value, onChange, disabled]);

  // Handle clear all selected items
  const handleClearAll = useCallback(() => {
    if (disabled) return;
    onChange([]);
  }, [onChange, disabled]);

  // Handle clear filtered items only
  const handleClearFiltered = useCallback(() => {
    if (disabled) return;

    const filteredValues = filteredOptions.map((option) => option.value);
    const newValue = value.filter((v) => !filteredValues.includes(v));
    onChange(newValue);
  }, [filteredOptions, value, onChange, disabled]);

  // Handle individual tag removal
  const handleTagRemove = useCallback(
    (valueToRemove, event) => {
      if (disabled) return;

      event?.stopPropagation();
      const newValue = value.filter((v) => v !== valueToRemove);
      onChange(newValue);
    },
    [value, onChange, disabled]
  );

  // Get display text for the main button
  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const selectedOption = options.find((opt) => opt.value === value[0]);
      return selectedOption ? selectedOption.label : placeholder;
    }
    return `${value.length} items selected`;
  };

  // Check if all filtered items are selected
  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => value.includes(option.value));

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    } else if (event.key === "ArrowDown" && !isOpen) {
      event.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <>
      <div className={`relative ${className}`} ref={containerRef}>
        {/* Label */}
        {label && (
          <label className=" bg-white px-2 text-sm font-medium text-teal-600 z-10">
            {label}
          </label>
        )}

        {/* Main Trigger Button */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={`
            w-full px-2 py-1 text-left bg-white border border-gray-300 rounded-lg shadow-sm 
            focus:outline-none focus:ring-0 focus:ring-slate-300 focus:border-slate-300
            transition-all duration-200 ease-in-out
            ${
              disabled
                ? "bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200"
                : "hover:bg-gray-50 hover:border-gray-400 cursor-pointer"
            }
            ${isOpen ? "border-slate-500 ring-2 ring-slate-200 shadow-md" : ""}
            flex items-center justify-between min-h-[40px]
          `}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={`${label || "MultiSelect"}, ${
            value.length
          } items selected`}
        >
          <span className="block truncate text-gray-900 items-center gap-2">
            <span className="truncate">{getDisplayText()}</span>
          </span>
          <i
            className={`fas fa-chevron-down text-gray-400 transition-transform duration-200 flex-shrink-0 ${
              isOpen ? "rotate-180" : ""
            }`}
          ></i>
        </button>

        {/* Selected Tags */}
        {showTags && value.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {value.map((selectedValue) => {
              const option = options.find(
                (opt) => opt?.value === selectedValue
              );
              return option ? (
                <span
                  key={selectedValue}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800 border border-teal-200 hover:bg-teal-200 transition-colors"
                >
                  <i className="fas fa-tag text-xs mr-1"></i>
                  <span className="max-w-xs truncate">{option.label}</span>
                  {!disabled && (
                    <button
                      onClick={(e) => handleTagRemove(selectedValue, e)}
                      className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-teal-300 focus:outline-none focus:bg-teal-300 transition-colors"
                      aria-label={`Remove ${option.label}`}
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  )}
                </span>
              ) : null;
            })}
          </div>
        )}

        {/* Selection Count Badge */}
        {value.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-teal-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-lg z-20 animate-pulse">
            {value.length > 99 ? "99+" : value.length}
          </span>
        )}

        {/* Dropdown Menu with Popper.js positioning */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="z-50 bg-white border border-gray-200 rounded-lg shadow-2xl opacity-0 invisible transform scale-95 transition-all duration-200 ease-out"
            style={{
              minWidth: triggerRef.current?.offsetWidth || "200px",
              maxWidth: "400px",
            }}
          >
            {/* Search Input */}
            {searchable && (
              <div className="p-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                <div className="relative">
                  <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"></i>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-0 focus:ring-slate-300 focus:border-slate-300 bg-white"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-10"
                      aria-label="Clear search"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {(selectAllOption || clearable) && (
              <div className="p-3 border-b border-gray-100 flex gap-4 bg-gray-50">
                {selectAllOption && (
                  <button
                    onClick={handleSelectAll}
                    disabled={allFilteredSelected || disabled}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    <i className="fas fa-check-double text-sm"></i>
                    Select All
                    {filteredOptions.length > 0 &&
                      `(${filteredOptions.length})`}
                  </button>
                )}
                {clearable && (
                  <button
                    onClick={searchTerm ? handleClearFiltered : handleClearAll}
                    disabled={
                      disabled ||
                      (searchTerm
                        ? filteredOptions.every(
                            (opt) => !value.includes(opt.value)
                          )
                        : value.length === 0)
                    }
                    className="text-sm font-medium text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    <i className="fas fa-eraser text-sm"></i>
                    {searchTerm ? "Clear Filtered" : "Clear All"}
                  </button>
                )}
              </div>
            )}

            {/* Options List */}
            <div
              className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              style={{ maxHeight }}
            >
              {filteredOptions.length === 0 ? (
                <div className="p-6 text-gray-500 text-center flex flex-col items-center gap-3">
                  <i className="fas fa-search text-4xl text-gray-300"></i>
                  <div>
                    <p className="text-sm font-medium">
                      {searchTerm
                        ? "No options match your search"
                        : "No options available"}
                    </p>
                    {searchTerm && (
                      <p className="text-xs text-gray-400 mt-1">
                        Try adjusting your search term
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div role="listbox" aria-multiselectable="true">
                  {filteredOptions.map((option, index) => {
                    const isSelected = value.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleItemToggle(option.value)}
                        className={`
                          flex items-center px-4 py-3 cursor-pointer transition-all duration-150
                          ${
                            isSelected
                              ? "bg-teal-50 text-teal-900 border-l-4 border-teal-500"
                              : "hover:bg-gray-50 text-gray-900 border-l-4 border-transparent hover:border-gray-200"
                          }
                          ${
                            index !== filteredOptions.length - 1
                              ? "border-b border-gray-50"
                              : ""
                          }
                        `}
                      >
                        <div className="flex items-center gap-3 w-full">
                          <div
                            className={`
                            w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200
                            ${
                              isSelected
                                ? "bg-teal-600 border-teal-600 scale-110"
                                : "border-gray-300 hover:border-teal-400"
                            }
                          `}
                          >
                            {isSelected && (
                              <i className="fas fa-check text-white text-sm"></i>
                            )}
                          </div>
                          <span className="flex-1 truncate font-medium">
                            {option.label}
                          </span>
                          {isSelected && (
                            <i className="fas fa-check-circle text-teal-600 animate-pulse"></i>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer Info */}
            {value.length > 0 && (
              <div className="p-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between rounded-b-lg">
                <div className="flex items-center gap-2">
                  <i className="fas fa-info-circle"></i>
                  <span>
                    {value.length} item{value.length !== 1 ? "s" : ""} selected
                  </span>
                </div>
                {filteredOptions.length > 0 && (
                  <span className="text-gray-400">
                    {filteredOptions.length} option
                    {filteredOptions.length !== 1 ? "s" : ""} available
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default MultiSelect;
